// ============================================================================
// Service-worker update → clean reload.
// ============================================================================
// src/sw.ts calls self.skipWaiting() + clients.claim() unconditionally, so a
// newly deployed service worker seizes control of already-open tabs the moment
// it activates. Without a matching client-side reload, that swaps the precache
// out from under a running page: the next lazy-loaded route chunk 404s (new
// hash, old precache), lazy-retry.ts reloads, and you land back on the stale
// shell — the "flashes the new version then blinks back to the old one" bug.
//
// Fix: when the controller actually changes (i.e. an updated SW took over, not
// the very first registration), reload once so the whole page comes back up on
// the new precache. Also poll registration.update() because the staff POS tab
// stays open for a whole shift and the browser's own update check is lazy.

const UPDATE_POLL_MS = 30 * 60 * 1000; // 30 min

export function initServiceWorkerAutoReload(): void {
  if (typeof navigator === 'undefined' || !('serviceWorker' in navigator)) return;

  // A controller already present at load means a SW was controlling this page
  // before — so a later controllerchange is a genuine update, not the initial
  // clients.claim() of a first-ever registration.
  const hadControllerAtLoad = Boolean(navigator.serviceWorker.controller);
  let reloading = false;

  navigator.serviceWorker.addEventListener('controllerchange', () => {
    if (!hadControllerAtLoad || reloading) return;
    reloading = true;
    window.location.reload();
  });

  navigator.serviceWorker.ready
    .then((registration) => {
      const poll = () => {
        registration.update().catch(() => {
          /* offline / transient — next tick retries */
        });
      };
      // Check for a new SW RIGHT NOW, on every launch. A home-screen PWA
      // (especially iOS standalone) does not reliably run the browser's own
      // update check when it's opened, so without an explicit update() on load
      // the user sees the stale shell until the 30-min tick — the "I have to
      // delete and reinstall the PWA to see a new deploy" report. update() here
      // pulls the new sw.js (served max-age=0, must-revalidate); src/sw.ts then
      // skipWaiting+claims and sw-update reloads on controllerchange.
      poll();
      window.setInterval(poll, UPDATE_POLL_MS);
      // Re-check whenever the app returns to the foreground. iOS standalone
      // doesn't fire both events dependably, so listen to both.
      document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'visible') poll();
      });
      window.addEventListener('focus', poll);
    })
    .catch(() => {
      /* no registration (SW disabled / unsupported) — nothing to poll */
    });
}
